import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ordersService } from "api/services/orders.service";
import ContentHeader from "components/Layout/ContentHeader";
import FulfillmentModal from "components/order-management/FulfillmentModal";
import ProductOrderCancelModal from "components/order-management/ProductOrderCancelModal";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { getApiErrorMessage } from "utils/api-error";
import { isDigitalProductOrder } from "utils/order";

import type { AdminOrder, OrderShippingInfo } from "api/types/order.types";
import "pages/AdminPage.scss";
import "./OrderDetail.scss";

const STATUS_LABEL: Record<string, string> = {
  CONFIRMED: "결제완료",
  PENDING: "결제대기",
  PAYMENT_ATTEMPTED: "결제시도",
  PAYMENT_PROCESSING: "결제처리중",
  PAYMENT_FAILED: "결제실패",
  CANCELLED: "취소됨",
  COMPLETED: "완료",
};
const STATUS_BADGE: Record<string, "active" | "pending" | "rejected" | "info" | "draft"> = {
  CONFIRMED: "active",
  PENDING: "pending",
  PAYMENT_ATTEMPTED: "pending",
  PAYMENT_PROCESSING: "info",
  PAYMENT_FAILED: "rejected",
  CANCELLED: "rejected",
  COMPLETED: "active",
};

const FULFILL_LABEL: Record<string, string> = {
  NEW: "신규",
  PREPARING: "배송준비",
  IN_TRANSIT: "배송중",
  DELIVERED: "배송완료",
  PURCHASE_CONFIRMED: "구매확정",
};
const FULFILL_BADGE: Record<string, "active" | "pending" | "info" | "rejected" | "draft"> = {
  NEW: "pending",
  PREPARING: "info",
  IN_TRANSIT: "info",
  DELIVERED: "active",
  PURCHASE_CONFIRMED: "draft",
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return "-";
  return value.replace("T", " ").slice(0, 16);
};

const formatAmount = (value: number | string | null | undefined) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return "-";
  return `${num.toLocaleString()}원`;
};

const formatAddress = (info: OrderShippingInfo | undefined | null) => {
  if (!info) return "-";
  const detail = info.address_detail ? ` ${info.address_detail}` : "";
  return `(${info.postal_code}) ${info.address}${detail}`;
};

interface FieldProps {
  label: string;
  full?: boolean;
  multiline?: boolean;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, full, multiline, children }) => (
  <div className={`order-detail-field${full ? " order-detail-field--full" : ""}`}>
    <span className="order-detail-field__label">{label}</span>
    <div
      className={`order-detail-field__value${multiline ? " order-detail-field__value--multiline" : ""}`}
    >
      {children}
    </div>
  </div>
);

const OrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<AdminOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 배송 상태 변경 모달
  const [showFulfillModal, setShowFulfillModal] = useState(false);
  // 주문 취소(환불) 모달
  const [showCancelModal, setShowCancelModal] = useState(false);

  const fetchOrder = async () => {
    if (!orderId) return;
    const id = Number(orderId);
    if (!Number.isFinite(id)) {
      setError("잘못된 주문 ID 입니다.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await ordersService.get(id);
      setOrder(res.data);
    } catch (err) {
      setOrder(null);
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const openFulfillModal = () => {
    if (!order) return;
    setShowFulfillModal(true);
  };

  const renderItems = (_shippingInfo: OrderShippingInfo | undefined) => {
    if (!order) return null;

    // 사용자 입력 옵션 질문/답변 (주문 객체 최상위, 답변 있는 것만)
    const inputPairs = (order.customer_input_pairs ?? []).filter(
      (p) => String(p?.answer ?? "").trim() !== "",
    );
    const inputPairsBlock =
      inputPairs.length > 0 ? (
        <div className="order-items__input-pairs">
          <div className="order-items__input-pairs-title">사용자 입력</div>
          {inputPairs.map((p, i) => (
            <div key={i} className="order-items__input-pair">
              {p.question && <span className="order-items__input-question">{p.question}</span>}
              <span className="order-items__input-answer">{p.answer}</span>
            </div>
          ))}
        </div>
      ) : null;

    if (order.items && order.items.length > 0) {
      return (
        <div className="order-items">
          {order.items.map((it) => {
            const optionText = it.item_details?.variant?.options
              ?.map((o) => `${o.group}: ${o.value}`)
              .join(", ");
            return (
              <div key={it.item_id} className="order-items__row">
                <div className="order-items__main">
                  <div className="order-items__name">{it.product_name}</div>
                  {optionText && <div className="order-items__option">{optionText}</div>}
                  {it.item_details?.variant?.variant_sku && (
                    <div className="order-items__sku">
                      SKU: {it.item_details.variant.variant_sku}
                    </div>
                  )}
                </div>
                <div className="order-items__qty">x{it.quantity}</div>
                <div className="order-items__price">
                  {formatAmount(it.subtotal)}
                  {Number(it.shipping_fee) > 0 && (
                    <div className="order-items__shipping">
                      배송비 {formatAmount(it.shipping_fee)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {inputPairsBlock}
        </div>
      );
    }

    // items 가 비어있는 단일 상품 주문 (product_info + order_details 사용)
    const pi = order.product_info;
    const finalPrice =
      pi?.discount_price && pi.discount_price !== "0" ? pi.discount_price : pi?.price;
    return (
      <div className="order-items">
        <div className="order-items__row">
          {pi?.thumbnail && <img className="order-items__thumb" src={pi.thumbnail} alt={pi.name} />}
          <div className="order-items__main">
            <div className="order-items__name">{pi?.name ?? "-"}</div>
            {pi?.brand && <div className="order-items__option">{pi.brand}</div>}
            {pi?.price && pi.discount_price && pi.discount_price !== pi.price && (
              <div className="order-items__option">
                <span style={{ textDecoration: "line-through" }}>{formatAmount(pi.price)}</span>
                {" → "}
                <strong>{formatAmount(pi.discount_price)}</strong>
              </div>
            )}
            {order.variant_info?.options && (
              <div className="order-items__option">{order.variant_info.options}</div>
            )}
            {order.variant_info?.sku && (
              <div className="order-items__sku">SKU: {order.variant_info.sku}</div>
            )}
          </div>
          <div className="order-items__qty">x{order.quantity}</div>
          <div className="order-items__price">{formatAmount(finalPrice ?? order.total_amount)}</div>
        </div>
        {inputPairsBlock}
      </div>
    );
  };

  const renderContent = () => {
    if (loading) return <div className="admin-page__loading">불러오는 중...</div>;
    if (error) return <div className="admin-page__error">{error}</div>;
    if (!order) return null;

    // 배송지: items[] 우선 → 없으면 order_details.shipping_info
    const itemShipping = order.items?.find((it) => it.item_details?.shipping_info)?.item_details
      ?.shipping_info;
    const shippingInfo: OrderShippingInfo | undefined =
      itemShipping ?? order.order_details?.shipping_info ?? undefined;

    const od = order.order_details;
    const hasBreakdown =
      !!od &&
      (od.product_subtotal !== undefined ||
        od.shipping_cost !== undefined ||
        od.base_shipping_fee !== undefined ||
        od.regional_surcharge !== undefined);

    return (
      <div className="order-detail">
        <section className="order-detail__section">
          <h4 className="order-detail__section-title">주문 정보</h4>
          <div className="order-detail__grid">
            <Field label="주문번호">{order.order_number}</Field>
            <Field label="주문 유형">{order.product_type ?? "-"}</Field>
            <Field label="주문일시">{formatDateTime(order.created_at)}</Field>
            <Field label="수정일시">{formatDateTime(order.updated_at)}</Field>
            <Field label="결제 금액">{formatAmount(order.total_amount)}</Field>
            <Field label="총 수량">{order.quantity}개</Field>
            {order.product_type === "TRAVEL" && (
              <Field label="투어 회차">
                {order.departure_id != null ? `#${order.departure_id}` : "-"}
              </Field>
            )}
            <Field label="결제 상태">
              <Badge variant={STATUS_BADGE[order.status] ?? "draft"}>
                {order.status_display ?? STATUS_LABEL[order.status] ?? order.status}
              </Badge>
            </Field>
            <Field label="배송 상태">
              {order.fulfillment_status ? (
                <Badge variant={FULFILL_BADGE[order.fulfillment_status] ?? "draft"}>
                  {order.fulfillment_status_display ??
                    FULFILL_LABEL[order.fulfillment_status] ??
                    order.fulfillment_status}
                </Badge>
              ) : (
                "-"
              )}
            </Field>
            <Field label="구매 확정">
              {order.purchase_confirmed_at ? formatDateTime(order.purchase_confirmed_at) : "미확정"}
            </Field>
            <Field label="가능한 액션">
              <div className="order-detail__flags">
                {order.can_cancel ? (
                  <Badge variant="info">취소 가능</Badge>
                ) : (
                  <Badge variant="draft">취소 불가</Badge>
                )}
                {order.can_purchase_confirm ? (
                  <Badge variant="active">구매확정 가능</Badge>
                ) : (
                  <Badge variant="draft">구매확정 불가</Badge>
                )}
                {order.can_review ? (
                  <Badge variant="active">리뷰 가능</Badge>
                ) : (
                  <Badge variant="draft">리뷰 불가</Badge>
                )}
              </div>
            </Field>
          </div>
        </section>

        {order.status === "CANCELLED" && (
          <section className="order-detail__section">
            <h4 className="order-detail__section-title">취소 내역</h4>
            <div className="order-detail__grid">
              <Field label="취소 일시">{formatDateTime(order.cancelled_at)}</Field>
              <Field label="처리자">{order.cancelled_by || "-"}</Field>
              <Field label="취소 사유" full multiline>
                {order.cancel_reason || "-"}
              </Field>
            </div>
          </section>
        )}

        <section className="order-detail__section">
          <h4 className="order-detail__section-title">고객 정보</h4>
          <div className="order-detail__grid">
            <Field label="이름">{order.user.name}</Field>
            <Field label="아이디">@{order.user.user_id}</Field>
            <Field label="이메일">{order.user.email || "-"}</Field>
            <Field label="휴대폰">{order.user.phone || "-"}</Field>
            <Field label="받는 사람">
              {shippingInfo
                ? `${shippingInfo.recipient_name} · ${shippingInfo.recipient_phone}`
                : "-"}
            </Field>
            <Field label="주소" full>
              {formatAddress(shippingInfo)}
            </Field>
            <Field label="배송 메모" full multiline>
              {shippingInfo?.shipping_memo || "-"}
            </Field>
          </div>
        </section>

        <section className="order-detail__section">
          <h4 className="order-detail__section-title">상품 정보</h4>
          {renderItems(shippingInfo)}
        </section>

        {(hasBreakdown || od?.inventory_deducted !== undefined) && (
          <section className="order-detail__section">
            <h4 className="order-detail__section-title">결제 내역</h4>
            <div className="order-detail__grid">
              {od?.product_subtotal !== undefined && (
                <Field label="상품 소계">{formatAmount(od.product_subtotal)}</Field>
              )}
              {od?.shipping_cost !== undefined && (
                <Field label="총 배송비">{formatAmount(od.shipping_cost)}</Field>
              )}
              {od?.base_shipping_fee !== undefined && (
                <Field label="기본 배송비">{formatAmount(od.base_shipping_fee)}</Field>
              )}
              {od?.regional_surcharge !== undefined && (
                <Field label="지역 추가 배송비">{formatAmount(od.regional_surcharge)}</Field>
              )}
              <Field label="결제 금액">
                <strong>{formatAmount(order.total_amount)}</strong>
              </Field>
              {od?.inventory_deducted !== undefined && (
                <Field label="재고 차감">
                  {od.inventory_deducted ? (
                    <Badge variant="active">차감됨</Badge>
                  ) : (
                    <Badge variant="draft">미차감</Badge>
                  )}
                </Field>
              )}
            </div>
          </section>
        )}

        <section className="order-detail__section">
          <div className="order-detail__section-header">
            <h4 className="order-detail__section-title">배송 정보</h4>
            {order.status !== "CANCELLED" &&
              order.product_type !== "TRAVEL" &&
              !isDigitalProductOrder(order) && (
              <Button size="sm" variant="outline" onClick={openFulfillModal}>
                배송 상태 변경
              </Button>
            )}
          </div>
          <div className="order-detail__grid">
            <Field label="택배사">{order.courier || "-"}</Field>
            <Field label="운송장 번호">{order.tracking_number || "-"}</Field>
            <Field label="배송 추적 URL" full>
              {order.tracking_url ? (
                <a
                  href={order.tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="order-detail__link"
                >
                  {order.tracking_url}
                </a>
              ) : (
                "-"
              )}
            </Field>
          </div>
        </section>

        {order.status_history && order.status_history.length > 0 && (
          <section className="order-detail__section">
            <h4 className="order-detail__section-title">상태 이력</h4>
            <ol className="order-history">
              {order.status_history.map((h, idx) => (
                <li key={`${h.status}-${h.changed_at}-${idx}`} className="order-history__item">
                  <div className="order-history__dot" />
                  <div className="order-history__main">
                    <div className="order-history__top">
                      <Badge variant={STATUS_BADGE[h.status] ?? "draft"}>
                        {STATUS_LABEL[h.status] ?? h.status}
                      </Badge>
                      <span className="order-history__time">{formatDateTime(h.changed_at)}</span>
                    </div>
                    <div className="order-history__by">처리자: {h.changed_by}</div>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
    );
  };

  return (
    <div className="admin-page">
      <ContentHeader
        title={order ? `주문 ${order.order_number}` : "주문 상세"}
        description="주문 상세 정보를 조회하고 배송 상태를 관리합니다."
        actions={[
          <Button
            key="back"
            variant="outline"
            onClick={() =>
              navigate(
                order?.product_type === "TRAVEL" ? "/travel-order-management" : "/order-management",
              )
            }
          >
            ← 목록으로
          </Button>,
          ...(order?.can_cancel
            ? [
                <Button key="cancel" variant="danger" onClick={() => setShowCancelModal(true)}>
                  주문 취소
                </Button>,
              ]
            : []),
        ]}
      />

      <div className="admin-page__card">{renderContent()}</div>

      <FulfillmentModal
        open={showFulfillModal}
        order={order}
        onClose={() => setShowFulfillModal(false)}
        onUpdated={fetchOrder}
      />

      <ProductOrderCancelModal
        open={showCancelModal}
        order={order}
        onClose={() => setShowCancelModal(false)}
        onCancelled={fetchOrder}
      />
    </div>
  );
};

export default OrderDetail;
